export const SKILL_MODAL_ENTRY_POINTS = [
	'chatSeeAll',
	'chatFullScreen',
	'studioBrowseSkills',
] as const;

/** Entry point for skill modal used for analytics. If implementing the skill modal in a new place, feel free to add a new entry point in SKILL_MODAL_ENTRY_POINTS */
export type SkillModalEntryPointPrefix = (typeof SKILL_MODAL_ENTRY_POINTS)[number];

type SkillModalView = `${SkillModalEntryPointPrefix}ModalSkill`;
type SkillModalCreateWithEditor = `${SkillModalEntryPointPrefix}ModalSkillCreateWithEditor`;
type SkillModalCreateWithChat = `${SkillModalEntryPointPrefix}ModalSkillCreateWithChat`;

/** Only ever viewed, never started. It offers several create flows side by side, so it is not tied to a session. */
export type SkillViewFlowTouchPoint = 'chatImportSkillButton' | SkillModalView;

export type SkillCreateFlowTouchPoint =
	| 'manualCreationLinks'
	| 'studioUniversalCreate'
	| 'studioCustomSkillsPage'
	| 'chatSkillListBuildSkill'
	| 'chatAutoSuggestBuildSkill'
	| 'chatTypeAheadBuildSkill'
	| 'chatCreateSkillWithChat'
	| 'chatCreateSkillWithEditor'
	| SkillModalCreateWithEditor
	| SkillModalCreateWithChat
	/** The chat drafted a skill on its own, rather than from a create-skill entry point. */
	| 'chatImplicitCustomSkill'
	/** A shared skill is being cloned from the receive shared skills modal. */
	| 'cloneSkill';

export type SkillUpdateFlowTouchPoint =
	| 'studioCustomSkillDetailsPage'
	| 'studioCustomSkillListUpdateAction'
	| 'chatSkillsListEditAction'
	| 'chatSkillCardRenderer';

export type SkillDeleteFlowTouchPoint =
	| 'agentStudioSkillListDeleteAction'
	| 'studioCustomSkillListDeleteAction'
	| 'chatSkillsListDeleteAction';

/** Why a skill create, update or delete did not go through. */
export type SkillFailureReason =
	| 'mutationErrors'
	| 'unsuccessfulResponse'
	| 'requestError'
	| 'duplicateName';

export type SkillFlowTouchPoint =
	| SkillCreateFlowTouchPoint
	| SkillUpdateFlowTouchPoint
	| SkillDeleteFlowTouchPoint;

// Possible session flows for custom skills
export type SessionFlowSlot =
	| 'chatSkill' // Create or update skill from chat
	| 'createModal' // Create skill from modal
	| 'createPage' // Create skill from page
	| 'updateModal' // update skill from modal
	| 'updatePage' // update skill from page
	| 'deleteDialog'; // dialog to delete skill

// Maps the skills modal touchpoints to its session slot. Don't need to modify this
const TOUCH_POINT_SESSION_SLOT_SKILL_MODAL = SKILL_MODAL_ENTRY_POINTS.reduce(
	(acc, curr) => {
		acc[`${curr}ModalSkillCreateWithEditor`] = 'createModal';
		return acc;
	},
	// Safe: the loop covers every entry in SKILL_MODAL_ENTRY_POINTS, which SkillModalCreateWithEditor derives from
	{} as Record<SkillModalCreateWithEditor, SessionFlowSlot>,
);

// Maps the skills modal touchpoints to its session slot. Don't need to modify this
const TOUCH_POINT_SESSION_SLOT_SKILL_CHAT = SKILL_MODAL_ENTRY_POINTS.reduce(
	(acc, curr) => {
		acc[`${curr}ModalSkillCreateWithChat`] = 'chatSkill';
		return acc;
	},
	// Safe: the loop covers every entry in SKILL_MODAL_ENTRY_POINTS, which SkillModalCreateWithChat derives from
	{} as Record<SkillModalCreateWithChat, SessionFlowSlot>,
);

// Each flow has its own session. So we are able to have multiple sessions at the same time
export const TOUCH_POINT_SESSION_SLOT: Record<SkillFlowTouchPoint, SessionFlowSlot> = {
	chatCreateSkillWithEditor: 'createModal',
	studioCustomSkillsPage: 'createPage',
	manualCreationLinks: 'createPage',
	studioUniversalCreate: 'createPage',
	chatCreateSkillWithChat: 'chatSkill',
	chatAutoSuggestBuildSkill: 'chatSkill',
	chatTypeAheadBuildSkill: 'chatSkill',
	chatSkillListBuildSkill: 'chatSkill',
	chatImplicitCustomSkill: 'chatSkill',
	cloneSkill: 'createModal',
	studioCustomSkillListUpdateAction: 'updatePage',
	studioCustomSkillDetailsPage: 'updatePage',
	chatSkillCardRenderer: 'updateModal',
	chatSkillsListEditAction: 'updateModal',
	studioCustomSkillListDeleteAction: 'deleteDialog',
	chatSkillsListDeleteAction: 'deleteDialog',
	agentStudioSkillListDeleteAction: 'deleteDialog',
	...TOUCH_POINT_SESSION_SLOT_SKILL_MODAL,
	...TOUCH_POINT_SESSION_SLOT_SKILL_CHAT,
};

/** How far the flow has progressed, named after the event that got it there. */
export type SkillCreateFlowStage = 'startedSkillFlow' | 'draftCardViewed';

export type SkillCreateFlowSessionState = {
	/** Joins every event of a single pass through the flow. */
	traceId: string;
	touchPoint: SkillFlowTouchPoint;
	stage: SkillCreateFlowStage;
};

export type FireSkillEventAttributes = {
	touchPoint?: SkillFlowTouchPoint | SkillViewFlowTouchPoint;
	traceId?: string;
	isUpdate?: boolean;
	failureReason?: SkillFailureReason;
	/** The skill a clone was made from. Only reported by the clone flow. */
	sourceSkillAri?: string;
	/** The skill a clone created. Only reported by the clone flow, when it succeeded. */
	newSkillAri?: string;
};

export type SkillFlowSessionHandle = {
	readonly slot: SessionFlowSlot;
	readonly state: SkillCreateFlowSessionState | undefined;
	advanceTo: (stage: SkillCreateFlowStage) => SkillFlowSessionHandle;
	clear: () => void;
};

// What is the source of the skill creation
export type SkillFlowSource = 'chat' | 'modal' | 'page';

export const CREATE_SOURCE_SESSION_SLOT: Record<SkillFlowSource, SessionFlowSlot> = {
	chat: 'chatSkill',
	modal: 'createModal',
	page: 'createPage',
};

export const UPDATE_SOURCE_SESSION_SLOT: Record<SkillFlowSource, SessionFlowSlot> = {
	chat: 'chatSkill',
	modal: 'updateModal',
	page: 'updatePage',
};
